import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Lock, Target, Bell, FileText, UserCircle, ArrowRight, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const features = [
    {
        icon: Lock,
        title: "Offres exclusives",
        description: "Accédez aux offres de VIE publiées uniquement sur les sites carrières des entreprises — jamais sur Business France. Soyez là où les autres ne regardent pas.",
        bgLight: "bg-violet-50",
        iconColor: "text-violet-600"
    },
    {
        icon: Target,
        title: "CV Matching",
        description: "Notre IA analyse votre CV et fait remonter les offres qui correspondent vraiment à votre profil, vos compétences et vos envies.",
        bgLight: "bg-blue-50",
        iconColor: "text-blue-600"
    },
    {
        icon: Bell,
        title: "Alertes intelligentes",
        description: "Notification dès qu'une nouvelle offre correspond à vos critères. Dans un marché « premier arrivé, premier servi », chaque heure compte.",
        bgLight: "bg-amber-50",
        iconColor: "text-amber-600"
    },
    {
        icon: FileText,
        title: "Mail de candidature IA",
        description: "Générez en 1 clic un mail de candidature percutant et personnalisé, adapté à l'offre et à votre parcours. Fini les heures de rédaction.",
        bgLight: "bg-emerald-50",
        iconColor: "text-emerald-600"
    },
    {
        icon: UserCircle,
        title: "Insights recruteurs",
        description: "Identifiez qui gère le recrutement VIE dans chaque entreprise. Multipliez vos canaux de contact (mail, LinkedIn) pour sortir de la pile de CV.",
        bgLight: "bg-rose-50",
        iconColor: "text-rose-600"
    }
];

const TrouvePlusPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-inter selection:bg-violet-500/30 flex flex-col relative overflow-hidden">
            <Helmet>
                <title>Augmentez vos chances de décrocher un VIE</title>
                <meta name="description" content="Offres VIE exclusives hors Business France, CV Matching IA, alertes temps réel et accès aux archives. Maximisez vos chances avec Trouve+." />
                <link rel="canonical" href="https://trouvetonvie.fr/trouve-plus" />
                <meta property="og:title" content="Augmentez vos chances de décrocher un VIE" />
                <meta property="og:description" content="Offres VIE exclusives hors Business France, CV Matching IA, alertes temps réel et accès aux archives. Maximisez vos chances avec Trouve+." />
                <meta property="og:url" content="https://trouvetonvie.fr/trouve-plus" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://trouvetonvie.fr/og-image.png" />
                <meta property="og:locale" content="fr_FR" />
                <meta property="og:site_name" content="Trouve ton VIE" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Augmentez vos chances de décrocher un VIE" />
                <meta name="twitter:description" content="Offres VIE exclusives hors Business France, CV Matching IA, alertes temps réel et accès aux archives. Maximisez vos chances avec Trouve+." />
                <meta name="twitter:image" content="https://trouvetonvie.fr/og-image.png" />
            </Helmet>

            {/* Background Decorations */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-15%] left-[-8%] w-[500px] h-[500px] bg-violet-100/50 rounded-full blur-[120px]" />
                <div className="absolute top-[10%] right-[-12%] w-[400px] h-[400px] bg-blue-100/40 rounded-full blur-[100px]" />
                <div className="absolute bottom-[-10%] left-[20%] w-[600px] h-[600px] bg-purple-100/30 rounded-full blur-[120px]" />
            </div>

            {/* ========== HERO SECTION ========== */}
            <div className="relative z-10 flex flex-col items-center text-center pt-16 md:pt-24 pb-12 px-6">

                {/* Badge early adopter */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-violet-600 text-white rounded-full shadow-lg shadow-violet-200 mb-8">
                    <Gift className="w-4 h-4" />
                    <span className="text-xs font-bold tracking-wide uppercase">Gratuit pour les premiers inscrits</span>
                </div>

                {/* Logo & Title */}
                <div className="mb-6 relative">
                    <h1 className="text-5xl md:text-7xl font-black text-slate-900 tracking-tight mb-2">
                        Trouve<span className="bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent">+</span>
                    </h1>
                    <div className="absolute -top-6 -right-14 rotate-12 bg-gradient-to-r from-violet-400 to-blue-400 text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow-lg hidden md:block">
                        Gratuit !
                    </div>
                </div>

                {/* Tagline */}
                <h2 className="text-xl md:text-2xl font-bold text-slate-800 max-w-2xl leading-snug">
                    Prenez une longueur d'avance dans la course au V.I.E.
                </h2>
                <p className="text-base md:text-lg text-slate-500 max-w-lg mt-3 leading-relaxed font-medium">
                    Trouve+ est disponible <span className="text-violet-600 font-bold">gratuitement</span> pour les premiers utilisateurs.<br />
                    <span className="text-slate-700">Il suffit de créer un compte pour y accéder.</span>
                </p>

                {/* CTA Section */}
                <div className="w-full max-w-md mt-10">
                    {user ? (
                        <div className="bg-white/80 backdrop-blur-sm border border-violet-200 rounded-2xl p-6 shadow-xl shadow-violet-900/5">
                            <div className="flex items-center justify-center gap-2 text-violet-700 font-bold mb-2">
                                <Sparkles className="w-5 h-5 fill-violet-400 text-violet-400" />
                                Vous avez déjà accès à Trouve+
                            </div>
                            <p className="text-sm text-slate-500 text-center mb-4">Retrouvez les fonctionnalités premium directement dans l'application.</p>
                            <button
                                onClick={() => navigate('/offres_vie')}
                                className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white px-6 py-3.5 rounded-xl font-bold transition-all shadow-lg shadow-violet-200 active:scale-95 flex items-center justify-center gap-2"
                            >
                                Voir les offres
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="bg-white/80 backdrop-blur-sm border border-slate-100/80 rounded-2xl p-6 shadow-xl shadow-violet-900/5">
                            <p className="text-sm font-medium text-slate-500 mb-4">
                                Aucune carte bancaire requise — accès complet immédiat.
                            </p>
                            <button
                                onClick={() => navigate('/onboarding')}
                                className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg shadow-violet-200 active:scale-95 flex items-center justify-center gap-2 text-lg"
                            >
                                <Sparkles className="w-5 h-5" />
                                Créer mon compte gratuitement
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <p className="text-xs text-slate-400 mt-3 text-center">
                                Déjà un compte ?{' '}
                                <button
                                    onClick={() => navigate('/login')}
                                    className="text-violet-600 font-bold hover:underline"
                                >
                                    Se connecter
                                </button>
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* ========== FEATURES SECTION ========== */}
            <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pb-20">

                <div className="text-center mb-10">
                    <p className="text-xs font-bold text-violet-500 uppercase tracking-[0.2em] mb-2">
                        5 outils inclus
                    </p>
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                        Tout pour décrocher <span className="bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent">votre</span> VIE
                    </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {features.map(({ icon: Icon, title, description, bgLight, iconColor }, i) => (
                        <div
                            key={i}
                            className="group relative p-6 rounded-2xl bg-white border border-slate-100/80 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                        >
                            <div className={`w-12 h-12 ${bgLight} rounded-xl flex items-center justify-center mb-4 ${iconColor} group-hover:scale-110 transition-transform duration-300`}>
                                <Icon className="w-6 h-6" />
                            </div>
                            <h4 className="text-lg font-bold text-slate-900 mb-2">{title}</h4>
                            <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
                            <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center">
                                <span className="text-[11px] font-bold text-slate-300">{i + 1}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ========== FOOTER TRUST ========== */}
            <div className="relative z-10 pb-12 pt-4">
                <div className="max-w-sm mx-auto pt-6 border-t border-slate-200/60">
                    <p className="text-xs text-slate-400 font-medium text-center">
                        🔒 Gratuit maintenant, sans engagement. L'offre est limitée aux premiers inscrits.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TrouvePlusPage;
